import React, { useState } from 'react';
import styles from './CoverageCheckInputView.module.scss';
import { ReactComponent as ArrowIcon } from 'app/assets/Icons/Chips/Arrow.svg';
import { ReactComponent as UploadIcon } from 'app/assets/Icons/Chips/Upload.svg';
import { Button } from '@zurich-primitives/button';
import {
  handleFileSelection,
  handleFileValidation,
  ICoverageCheckFiles
} from 'app/utils';
import {
  getCoverageCheckV2Id,
  getIsLoading,
  getService,
  setCoverageCheckV2Files,
  setCoverageCheckV2Id,
  setGeneratingExtraction,
  useAppSelector
} from 'app/redux';
import config from 'config';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { UploadedFileCard } from 'app/components/Sections/UploadedDocument/UploadedDocument';
import { LoadingSpinner } from 'app/components';
import { Tooltip } from 'react-tooltip';
import { FileType } from './index';
import { UNDEFINED_FILES } from 'app/utils/consts';

export const CoverageCheckV2InputView: React.FC = () => {
  const currentService = useAppSelector(getService);
  const MANDATORY_DOCUMENTS =
    'claims notification, policy contract, terms & conditions';
  const FILE_SIZE_LIMIT_IN_MB = config.APP.MAX_FILESIZE;
  const ACCEPTED_FILES = 'PDF, JPG';
  const uploadFileButtons = [
    { label: 'Claims notification', key: 'claims' },
    { label: 'Policy contract', key: 'policy' },
    { label: 'Terms & Conditions', key: 'terms' },
    { label: 'Extra files', key: 'extra' }
  ];

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [uploadedFiles, setUploadedFiles] =
    useState<ICoverageCheckFiles>(UNDEFINED_FILES);

  const coverageCheckId = useAppSelector(getCoverageCheckV2Id);
  const [claimId, setClaimId] = useState<string | undefined>(coverageCheckId);
  const isLoading = useAppSelector(getIsLoading);

  const tooltipContent = `
  <div className={styles['tooltip-content']}>
    <div className={styles['text-content']}>
      <p>
        <strong>Please fill in all required fields</strong>
      </p>
      <p>Required field: Claim ID</p>
      <p>Mandatory document: claims notification, policy contract, terms & conditions</p>
    </div>
  </div>`;

  const isAbleToRun =
    uploadedFiles.claims && uploadedFiles.policy && uploadedFiles.terms;

  const handleSetClaimIdInput = (inputClaimId: string): void => {
    setClaimId(inputClaimId);
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: FileType
  ): void => {
    const { files } = e.target;
    const selectedFile: File | undefined = files ? files[0] : undefined;

    if (selectedFile !== undefined) {
      const validation = handleFileValidation(selectedFile, currentService);
      if (validation.valid) {
        setUploadedFiles((prevFiles) => ({
          ...prevFiles,
          [fileType]: selectedFile
        }));
        return;
      }
      const errorMessage = validation.error;
      console.error(errorMessage);
    }
  };

  const handleSelectionClick = (
    e: { stopPropagation: () => void },
    fileType: FileType
  ): void => {
    e.stopPropagation();
    handleFileSelection(
      (e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e, fileType),
      currentService
    );
  };

  const handleGoToCoverageCheckSummary = (): void => {
    dispatch(setCoverageCheckV2Files(uploadedFiles));
    dispatch(setGeneratingExtraction(true));
    dispatch(setCoverageCheckV2Id(claimId));
    setClaimId(undefined);
    setUploadedFiles(UNDEFINED_FILES);
    navigate(config.APP.PATH.SUMMARY);
  };

  const handleDeleteFile = (fileType: FileType): void => {
    setUploadedFiles((prevFiles) => ({
      ...prevFiles,
      [fileType]: undefined
    }));
  };

  const renderCoverageCheckTitle = (): React.JSX.Element => {
    return (
      <div className={styles['coverage-check-v2-title']}>
        <span className={styles['coverage-check-v2-blue-text']}>
          Coverage Check
        </span>
        <span className={styles['coverage-check-v2-dark-text']}> Tool</span>
      </div>
    );
  };

  const renderAllUploadFileButtons = (): React.JSX.Element => (
    <div className={styles['browse-file-section']}>
      {uploadFileButtons.map(({ label, key }) => (
        <div key={key} className={styles['browse-file-container']}>
          {renderUploadFileButton(label, key as FileType)}
        </div>
      ))}
    </div>
  );

  const renderUploadFileButton = (
    title: string,
    fileType: FileType
  ): React.JSX.Element => {
    const isFileUploaded = uploadedFiles[fileType] instanceof File;
    const file = uploadedFiles[fileType];

    return (
      <React.Fragment key={fileType}>
        {isFileUploaded && file ? (
          <>
            <p className={styles['title-browse-file']}>{title}</p>
            <UploadedFileCard
              file={file}
              showDelete
              lenghtTextDisplay={30}
              borderRadiusRounded
              handleDeleteFile={() => handleDeleteFile(fileType)}
            />
          </>
        ) : (
          <Button
            variant={'secondary'}
            onClick={(e: { stopPropagation: () => void }) =>
              handleSelectionClick(e, fileType)
            }
            className={styles['browse-file']}
          >
            <Button.Icon>
              <UploadIcon />
            </Button.Icon>
            <Button.Label>{title}</Button.Label>
          </Button>
        )}
      </React.Fragment>
    );
  };

  const renderRunCoverageCheckButton = (): React.JSX.Element => (
    <div style={{ margin: '1rem 0' }}>
      <Button
        onClick={handleGoToCoverageCheckSummary}
        disabled={!isAbleToRun || !claimId}
        className={styles['summary-button']}
      >
        <Button.Label>
          <p
            data-tooltip-id="file-required-options"
            data-tooltip-html={tooltipContent}
            data-tooltip-place="bottom"
          >
            Run coverage check
          </p>
        </Button.Label>
        <Button.Icon>
          <ArrowIcon />
        </Button.Icon>
      </Button>
      {(!isAbleToRun || !claimId) && (
        <Tooltip
          id="file-required-options"
          className={styles['info-required-fields']}
        />
      )}
    </div>
  );

  return (
    <div className={styles['coverage-check-v2-container']}>
      {renderCoverageCheckTitle()}
      <h2>Identifier</h2>
      <h4>Claim ID</h4>

      <input
        type="text"
        placeholder="Indicate claim ID"
        className={styles.input}
        spellCheck="false"
        value={claimId}
        onChange={(e) => handleSetClaimIdInput(e.target.value)}
      />

      <h2>Documents</h2>
      <div className={styles['documents-info']}>
        <div className={styles['documents-text']} style={{ marginLeft: '0' }}>
          <ArrowIcon style={{ margin: '0 0.5rem 0 0' }} />
          <p style={{ margin: '0' }}>
            Accepted file format: <strong>{ACCEPTED_FILES}</strong>
          </p>
        </div>

        <div className={styles['documents-text']}>
          <ArrowIcon style={{ marginRight: '0.5rem' }} />
          <p style={{ margin: '0' }}>
            File size limit:{' '}
            <strong>up to {FILE_SIZE_LIMIT_IN_MB} MB per file</strong>
          </p>
        </div>

        <div className={styles['documents-text']}>
          <ArrowIcon style={{ margin: '0 0.5rem 0 0' }} />
          <p style={{ margin: '0' }}>
            Mandatory documents: <strong> {MANDATORY_DOCUMENTS}</strong>
          </p>
        </div>
      </div>
      {renderAllUploadFileButtons()}

      {renderRunCoverageCheckButton()}
      {isLoading && <LoadingSpinner />}
    </div>
  );
};
